import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';

import { useRouter } from 'next/router';

import { parseCookies, setCookie } from 'nookies';
import decode from 'jwt-decode';
import { AxiosError } from 'axios';

import { routes } from '~/shared/constants/routes';
import { cookies } from '~/shared/constants/cookies';

import { useToast } from '~/shared/hooks/useToast';

import { ISelectOption } from '~/shared/components/Form/Select/types';

import { getUserWhitelabelResale } from '~/shared/utils/getUserWhitelabelResale';
import { getUserPermissionsIds } from '~/shared/utils/getUserPermissionsIds';
import { getAnyValidRouteWithPermission } from '~/shared/utils/getAnyValidUserPermission';

import { SignInRequestDTO } from '~/shared/services/api/dtos/SignInRequestDTO';
import {
  signIn as apiSignIn,
  loadUserById,
  loadUserResale,
  setApiDefaults,
} from '~/shared/services/api';

import { WithChildren } from '~/shared/types/WithChildren';
import { IResale } from '~/modules/resales/interfaces/IResale';
import { IUserAccess } from '../interfaces/IUserAccess';
import { IUser } from '../interfaces/IUser';
import { IDecodedToken } from '../interfaces/IDecodedToken';

interface IAuthContextData {
  user: IUser;
  signIn(data: SignInRequestDTO): Promise<IUser>;
  signOut(): void;
  isAuthenticated: boolean;
  currentAccess: IUserAccess;
  currentAccessId: string;
  currentAccessResale: IResale;
  setCurrentAccessId(id: string): void;
  accessOptions: ISelectOption[];
  hasResale: boolean;
  isLoading: boolean;
}

interface IAuthProviderProps {
  currentAccessResale: IResale;
  currentAccessId: string;
}

const AuthContext = createContext({} as IAuthContextData);

const AuthProvider: WithChildren<IAuthProviderProps> = ({
  children,
  currentAccessResale: initialCurrentAccessResale,
  currentAccessId: initialCurrentAccessId,
}) => {
  const router = useRouter();
  const toast = useToast();

  const [user, setUser] = useState<IUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentAccess, setCurrentAccess] = useState<IUserAccess>(null);
  const [currentAccessId, setCurrentAccessId] = useState<string>(
    initialCurrentAccessId || ''
  );
  const [currentAccessResale, setCurrentAccessResale] = useState<IResale>(
    initialCurrentAccessResale || null
  );

  const isAuthenticated = useMemo(() => !!user, [user]);

  const hasResale = useMemo(
    () => Object.values(currentAccessResale || {}).length > 0,
    [currentAccessResale]
  );

  const accessOptions: ISelectOption[] = useMemo(
    () =>
      user?.access?.map((access) => ({
        value: access._id,
        label: access.resale?.name,
      })),
    [user]
  );

  const isFirstAccess = useRef<boolean>(true);

  const signOut = useCallback(async () => {
    setApiDefaults(null, null, null);
    await router.push(routes.AUTH.SIGN_IN);
    setUser(null);
    setCurrentAccess(null);
    setCurrentAccessId(null);
    setCurrentAccessResale(null);
    setIsLoading(false);
  }, [router]);

  const signIn = useCallback(
    async (data: SignInRequestDTO) => {
      try {
        const apiResponse = await apiSignIn(data);

        const apiUser = apiResponse.user as unknown as IUser;

        setCookie(null, cookies.AUTH_TOKEN, apiResponse.token, { path: '/' });
        setCookie(null, cookies.CURRENT_ACCESS_ID, apiUser.access[0]._id, {
          path: '/',
        });

        const resale = (await loadUserResale()) as unknown as IResale;

        setCookie(null, cookies.CURRENT_ACCESS_RESALE, JSON.stringify(resale), {
          path: '/',
        });

        const userPermissionsIds = getUserPermissionsIds(
          apiUser.access[0],
          resale
        );
        const whiteLabelResale = getUserWhitelabelResale(resale);
        setApiDefaults(apiResponse.token, userPermissionsIds, whiteLabelResale);

        setUser(apiUser);
        setCurrentAccess(apiUser.access[0]);
        setCurrentAccessId(apiUser.access[0]._id);
        setCurrentAccessResale(resale);
        const validRoute = getAnyValidRouteWithPermission(userPermissionsIds);

        setCookie(null, cookies.USERNAME, apiUser?.name, {
          path: '/',
        });

        if (!validRoute) {
          toast.show({
            variant: 'warning',
            title: `Ops, você não tem permissão para acessar o sistema!`,
            description: 'Entre em contato com um administrator.',
          });

          signOut();
          return;
        }

        router.push(validRoute);

        return apiUser;
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 401) {
          toast.show({
            title: 'Ops, credenciais inválida(s)!',
            description: 'Seu e-mail e/ou senha está(ão) incorreto(s).',
            variant: 'error',
          });
          return;
        }

        toast.show({
          title: 'Ops, não foi possível fazer o login!',
          description: 'Recarregue a página e tente novamente.',
          variant: 'error',
        });
      }
    },
    [router, signOut, toast]
  );

  useEffect(() => {
    (async () => {
      const { [cookies.AUTH_TOKEN]: token } = parseCookies();

      if (currentAccessId) {
        setCookie(null, cookies.CURRENT_ACCESS_ID, currentAccessId, {
          path: '/',
        });
      }

      if (token) {
        if (isFirstAccess.current && user) {
          isFirstAccess.current = false;
          return;
        }

        isFirstAccess.current = false;

        try {
          const { _id: userId } = decode<IDecodedToken>(token);

          setIsLoading(true);

          const userResponse = (await loadUserById(userId)) as unknown as IUser;
          const resaleResponse = (await loadUserResale()) as unknown as IResale;

          setCurrentAccessResale(resaleResponse);
          setCookie(
            null,
            cookies.CURRENT_ACCESS_RESALE,
            JSON.stringify(resaleResponse),
            {
              path: '/',
            }
          );

          if (userResponse) {
            const userCurrentAccess = userResponse?.access?.find(
              (access) => access._id === currentAccessId
            );

            const userPermissionsIds = getUserPermissionsIds(
              userCurrentAccess,
              resaleResponse
            );
            const whiteLabelResale = getUserWhitelabelResale(resaleResponse);

            setApiDefaults(token, userPermissionsIds, whiteLabelResale);

            setCurrentAccess(userCurrentAccess);
            setUser(userResponse);
          } else {
            signOut();
          }
        } catch {
          signOut();
        } finally {
          setIsLoading(false);
        }
      }
    })();
  }, [currentAccessId]);

  return (
    <AuthContext.Provider
      value={{
        user,
        signIn,
        signOut,
        isAuthenticated,
        currentAccess,
        currentAccessId,
        currentAccessResale,
        setCurrentAccessId,
        accessOptions,
        hasResale,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = (): IAuthContextData => {
  return useContext(AuthContext);
};

export { AuthProvider, useAuth };
